import { useMemo } from "react";
import { differenceInCalendarDays, parseISO, addDays, format, startOfWeek } from "date-fns";
import { PMBOK_GROUPS, phaseColor, statusMeta, MS_SHAPES } from "@/lib/constants";
import { CalendarRange } from "lucide-react";

const LABEL_W = 260;
const HEADER_H = 36;
const PHASE_H = 30;
const ROW_H = 44;
const BAR_H = 24;

export default function GanttTimeline({ tasks, onOpenTask, phases, phaseColors, criticalIds = [], colorOf: colorOfProp, timescaleColor = "#132A5E", fontColor = "", fontSize = 11, holidays = {}, dayW = 30, hideDates = false, labelWrap = 200 }) {
  const DAY_W = dayW;
  const dated = tasks.filter((t) => t.start_date && t.end_date);
  const critical = new Set(criticalIds);
  const phaseList = phases && phases.length ? phases : PMBOK_GROUPS;
  const colorOf = colorOfProp || ((p) => (phaseColors && phaseColors[p]) || phaseColor(p, phaseList.indexOf(p)));

  const range = useMemo(() => {
    if (!dated.length) return null;
    let min = parseISO(dated[0].start_date);
    let max = parseISO(dated[0].end_date);
    dated.forEach((t) => {
      const s = parseISO(t.start_date), e = parseISO(t.end_date);
      if (s < min) min = s;
      if (e > max) max = e;
    });
    min = startOfWeek(min, { weekStartsOn: 1 });
    const total = differenceInCalendarDays(max, min) + 5;
    return { min, total };
  }, [dated]);

  const layout = useMemo(() => {
    if (!range) return { rows: [], pos: {}, totalH: 0 };
    const grouped = phaseList
      .map((g) => ({ phase: g, items: dated.filter((t) => t.phase === g) }))
      .filter((g) => g.items.length);
    const rows = [];
    const pos = {};
    let y = 0;
    grouped.forEach((grp) => {
      rows.push({ type: "phase", phase: grp.phase, top: y });
      y += PHASE_H;
      grp.items.forEach((t) => {
        const s = parseISO(t.start_date);
        const offset = differenceInCalendarDays(s, range.min);
        const len = differenceInCalendarDays(parseISO(t.end_date), s) + 1;
        const x1 = offset * DAY_W;
        const w = Math.max(len * DAY_W - 4, 16);
        pos[t.id] = { x1, x2: x1 + w, w, cy: y + ROW_H / 2 };
        rows.push({ type: "task", task: t, top: y });
        y += ROW_H;
      });
    });
    return { rows, pos, totalH: y };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, dated, phaseList, DAY_W]);

  if (!dated.length) {
    return (
      <div className="border border-border rounded-lg p-12 text-center" data-testid="gantt-empty">
        <CalendarRange className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No scheduled tasks yet. Add start &amp; end dates (or build a plan) to see the timeline.</p>
      </div>
    );
  }

  const weeks = [];
  for (let d = 0; d < range.total; d += 7) {
    weeks.push({ offset: d, label: format(addDays(range.min, d), "MMM d") });
  }

  const arrows = [];
  dated.forEach((t) => {
    (t.dependencies || []).forEach((dep) => {
      const pid = typeof dep === "string" ? dep : dep.predecessor_id;
      const dtype = typeof dep === "string" ? "FS" : (dep.type || "FS");
      if (layout.pos[pid] && layout.pos[t.id]) {
        arrows.push({ key: `${pid}-${t.id}`, type: dtype, from: layout.pos[pid], to: layout.pos[t.id] });
      }
    });
  });

  const chartW = range.total * DAY_W;

  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid="gantt-chart">
      <div className="overflow-x-auto">
        <div style={{ width: LABEL_W + chartW, minWidth: "100%" }}>
          {/* header */}
          <div className="flex border-b border-border" style={{ background: timescaleColor }} data-testid="gantt-timescale-header">
            <div style={{ width: LABEL_W }} className="shrink-0 px-4 py-2 text-xs font-semibold text-white/90 border-r border-white/20">TASK</div>
            <div className="relative" style={{ width: chartW, height: HEADER_H }}>
              {weeks.map((w) => (
                <div key={w.offset} className="absolute top-0 h-full border-l border-white/20 text-white/85 px-1 pt-2" style={{ left: w.offset * DAY_W, fontSize }}>
                  {w.label}
                </div>
              ))}
            </div>
          </div>

          {/* body */}
          <div className="flex">
            {/* labels */}
            <div style={{ width: LABEL_W }} className="shrink-0 border-r border-border">
              {layout.rows.map((r, i) =>
                r.type === "phase" ? (
                  <div key={i} style={{ height: PHASE_H }} className="flex items-center px-4 bg-secondary/20 border-b border-border">
                    <span className="overline" style={{ color: fontColor || colorOf(r.phase), fontSize: fontSize - 1 }}>{r.phase}</span>
                  </div>
                ) : (
                  <button key={i} onClick={() => onOpenTask(r.task)} style={{ height: ROW_H }} className="w-full flex items-center px-4 border-b border-border/60 truncate text-left hover:bg-secondary/30" title={r.task.title}>
                    <span className="truncate" style={{ fontSize: fontSize + 2, color: fontColor || undefined }}>{(r.task.status === "done" || (r.task.percent_complete || 0) >= 100) ? "✓ " : ""}{r.task.title}</span>
                  </button>
                )
              )}
            </div>

            {/* timeline */}
            <div className="relative" style={{ width: chartW, height: layout.totalH }}>
              {/* gridlines */}
              {weeks.map((w) => (
                <div key={w.offset} className="absolute top-0 bottom-0 border-l border-border/40" style={{ left: w.offset * DAY_W }} />
              ))}
              {/* non-working day shading (weekends + holidays) */}
              {Array.from({ length: Math.min(range.total, 1500) }, (_, d) => {
                const day = addDays(range.min, d);
                const wknd = day.getDay() === 0 || day.getDay() === 6;
                const hol = holidays[format(day, "yyyy-MM-dd")];
                if (!wknd && !hol) return null;
                return <div key={`nw-${d}`} className="absolute top-0 bottom-0 pointer-events-none" title={hol || undefined}
                  style={{ left: d * DAY_W, width: DAY_W, background: hol ? "rgba(245,158,11,0.14)" : "rgba(100,116,139,0.09)" }} data-testid={hol ? `gantt-holiday-${format(day, "yyyy-MM-dd")}` : undefined} />;
              })}
              {/* row bands */}
              {layout.rows.map((r, i) =>
                r.type === "phase" ? (
                  <div key={i} className="absolute left-0 right-0 bg-secondary/20 border-b border-border" style={{ top: r.top, height: PHASE_H }} />
                ) : (
                  <div key={i} className="absolute left-0 right-0 border-b border-border/40" style={{ top: r.top, height: ROW_H }} />
                )
              )}

              {/* dependency arrows */}
              <svg className="absolute inset-0 pointer-events-none" width={chartW} height={layout.totalH} data-testid="gantt-dependencies">
                <defs>
                  <marker id="dep-arrow" markerWidth="9" markerHeight="9" refX="6" refY="3" orient="auto" markerUnits="userSpaceOnUse">
                    <path d="M0,0 L6,3 L0,6 Z" fill="#475569" />
                  </marker>
                </defs>
                {arrows.map((a) => {
                  const sx = (a.type === "SS" || a.type === "SF") ? a.from.x1 : a.from.x2;
                  const sy = a.from.cy;
                  const tx = (a.type === "FF" || a.type === "SF") ? a.to.x2 : a.to.x1;
                  const ty = a.to.cy;
                  const stub = sx + 11;
                  const d = `M ${sx} ${sy} H ${stub} V ${ty} H ${tx - 7}`;
                  return (
                    <g key={a.key}>
                      <path d={d} fill="none" stroke="#475569" strokeWidth="1.4" strokeDasharray="4 3" markerEnd="url(#dep-arrow)" opacity="0.65" />
                      {a.type !== "FS" && (
                        <text x={stub + 2} y={Math.min(sy, ty) - 3} fontSize="9" fill="#475569" opacity="0.85">{a.type}</text>
                      )}
                    </g>
                  );
                })}
              </svg>

              {/* bars */}
              {layout.rows.filter((r) => r.type === "task").map((r) => {
                const t = r.task;
                const p = layout.pos[t.id];
                const sm = statusMeta(t.status);
                const isCrit = critical.has(t.id);
                if (t.is_milestone) {
                  const glyph = MS_SHAPES[t.ms_shape];
                  const mcolor = t.ms_color || colorOf(t.phase);
                  return (
                    <button
                      key={t.id}
                      onClick={() => onOpenTask(t)}
                      className="absolute flex items-center gap-1.5 hover:brightness-110 transition-all"
                      style={{ left: p.x1 - 7, top: r.top + (ROW_H - 16) / 2, height: 16 }}
                      data-testid={`gantt-bar-${t.id}`}
                      title={`◆ ${t.title} · ${t.start_date}`}
                    >
                      {!hideDates && t.start_date && (
                        <span className="absolute right-full mr-1.5 tabular-nums font-semibold whitespace-nowrap" style={{ fontSize: Math.max(9, fontSize - 2), color: fontColor || "#64748b", top: "50%", transform: "translateY(-50%)" }} data-testid={`gantt-ms-date-${t.id}`}>
                          {format(parseISO(t.start_date), "MM/dd")}
                        </span>
                      )}
                      {glyph
                        ? <span className="text-sm leading-none shrink-0" style={{ color: mcolor, textShadow: isCrit ? "0 0 0 2px rgba(17,17,17,0.65)" : "none" }}>{glyph}</span>
                        : <span className="block h-3.5 w-3.5 rotate-45" style={{ background: mcolor, boxShadow: isCrit ? "0 0 0 2px rgba(17,17,17,0.65)" : "none" }} />}
                      <span className="font-medium text-left leading-tight line-clamp-2" style={{ fontSize, color: fontColor || undefined, maxWidth: labelWrap }}>{(t.status === "done" || (t.percent_complete || 0) >= 100) ? "✓ " : ""}{t.title}</span>
                    </button>
                  );
                }
                return (
                  <button
                    key={t.id}
                    onClick={() => onOpenTask(t)}
                    className="absolute rounded-md flex items-center px-2 font-medium text-white overflow-hidden hover:brightness-110 transition-all shadow-sm"
                    style={{ left: p.x1, width: p.w, top: r.top + (ROW_H - BAR_H) / 2, height: BAR_H, background: colorOf(t.phase), fontSize, outline: isCrit ? "2px solid rgba(17,17,17,0.7)" : "none", outlineOffset: "-1px" }}
                    data-testid={`gantt-bar-${t.id}`}
                    title={`${isCrit ? "★ CRITICAL · " : ""}${t.title} · ${t.start_date} → ${t.end_date}`}
                  >
                    <span className="h-2 w-2 rounded-full mr-1.5 shrink-0" style={{ background: sm.color }} />
                    <span className="truncate">{(t.status === "done" || (t.percent_complete || 0) >= 100) ? "✓ " : ""}{t.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
